import { AnimatePresence, motion } from "framer-motion"
import { Clock, Search, X } from "lucide-react"
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { fadeIn } from "@/shared/lib/motion"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"

interface PostsSearchBarProps {
	value: string
	onChange: (value: string) => void
	onSearch: () => void
	inputRef?: RefObject<HTMLInputElement | null>
	trailing?: ReactNode
	/**
	 * Живой поиск: `onSearch` срабатывает сам через 300 мс простоя вместо
	 * явной кнопки «Найти», она пропадает, Enter только закрывает клавиатуру.
	 * Опционально, чтобы не менять поведение страниц, которые ещё жмут «Найти» явно.
	 */
	autoSearch?: boolean
	/** Недавние запросы: список под полем при фокусе на пустом значении. */
	recentQueries?: string[]
	onPickRecent?: (value: string) => void
}

/**
 * Search input used to filter posts. In the default mode Enter or the
 * "Найти" button triggers `onSearch`. With `autoSearch`, the button is
 * replaced by a 300ms debounce that calls `onSearch` on its own, and an
 * empty focused field can show recent queries.
 */
export function PostsSearchBar({
	value,
	onChange,
	onSearch,
	inputRef,
	trailing,
	autoSearch = false,
	recentQueries,
	onPickRecent,
}: PostsSearchBarProps) {
	const [focused, setFocused] = useState(false)
	const debouncedValue = useDebounced(value, 300)
	const isFirstRun = useRef(true)
	const onSearchRef = useRef(onSearch)
	onSearchRef.current = onSearch

	// biome-ignore lint/correctness/useExhaustiveDependencies: debouncedValue: триггер повтора, а не данные эффекта; onSearch берётся из рефа
	useEffect(() => {
		if (!autoSearch) return
		if (isFirstRun.current) {
			isFirstRun.current = false
			return
		}
		onSearchRef.current()
	}, [debouncedValue, autoSearch])

	const showRecent = autoSearch && focused && !value && !!recentQueries?.length

	return (
		<div className="flex gap-2">
			<div className="relative min-w-0 flex-1">
				<Search className="absolute top-1/2 left-3 z-30 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					ref={inputRef}
					type="search"
					placeholder="Поиск материалов"
					aria-label="Поиск материалов"
					enterKeyHint="search"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					onKeyDown={(e) => {
						if (e.key !== "Enter") return
						if (!autoSearch) onSearch()
						e.currentTarget.blur()
					}}
					className="relative z-30 h-10 rounded-xl border-border/60 bg-card/50 pl-9 pr-12 sm:h-11"
				/>
				{value && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label="Очистить поиск"
						onClick={() => onChange("")}
						className="absolute right-0 top-0 z-30 h-full w-11 rounded-none hover:bg-transparent"
					>
						<X className="size-4" />
					</Button>
				)}

				<AnimatePresence>
					{showRecent && (
						<motion.div
							aria-hidden
							className="fixed inset-0 z-20 bg-background/70 backdrop-blur-sm"
							variants={fadeIn}
							initial="hidden"
							animate="visible"
							exit="hidden"
						/>
					)}
				</AnimatePresence>

				{showRecent && (
					<div className="absolute top-full right-0 left-0 z-30 mt-1.5 overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
						{recentQueries?.map((query) => (
							<button
								key={query}
								type="button"
								// onMouseDown, а не onClick, иначе blur поля снимает showRecent раньше клика
								onMouseDown={(e) => {
									e.preventDefault()
									onPickRecent?.(query)
								}}
								className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-secondary/60"
							>
								<Clock className="size-4 shrink-0 text-muted-foreground" />
								<span className="min-w-0 truncate">{query}</span>
							</button>
						))}
					</div>
				)}
			</div>
			{!autoSearch && (
				<Button type="button" onClick={onSearch} className="h-10 shrink-0 rounded-xl sm:h-11">
					Найти
				</Button>
			)}
			{trailing}
		</div>
	)
}
